import React from 'react';
import { Formik, Form } from 'formik';
import { object, string } from 'yup';
import {
  Box,
  TextArea,
  TextInput,
  InputLabel,
  InputDescription,
  Button,
  Checkbox,
  Flex,
} from '@fullstory/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';

const invalidEmailAddress = intl.formatMessage({
  defaultMessage: 'Please enter a valid email address',
  id: 'oLpv29',
});
const validationSchema = object().shape({
  email: string().email(invalidEmailAddress).required(invalidEmailAddress),
});

interface SendSnippetValues {
  email: string;
  message: string;
  invite: boolean; // True to invite the user to the org
}

export interface SendSnippetFormProps {
  onSubmit: (emailTo: string, message: string, invite: boolean) => void;
  showInviteOption: boolean;
  mode: 'snippet' | 'mobile-setup';
}

export const SendSnippetForm: React.FC<SendSnippetFormProps> = ({
  onSubmit,
  showInviteOption,
  mode,
}) => {
  const intl = useIntl();
  return (
    <Formik<SendSnippetValues>
      initialValues={{ email: '', message: '', invite: true }}
      validationSchema={validationSchema}
      onSubmit={(vals: SendSnippetValues) => onSubmit(vals.email, vals.message, vals.invite)}
    >
      {({ errors, touched, values, handleChange, setTouched, setValues }) => (
        <Form>
          <Box marginBottom={16}>
            <InputLabel htmlFor="email">
              {mode == 'snippet' ? (
                <FormattedMessage
                  defaultMessage="Who are you sending this snippet to?"
                  id="u2rvds"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Who are you sending mobile setup information to?"
                  id="2Ob6a4"
                />
              )}
            </InputLabel>
            <TextInput
              placeholder={intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' })}
              id="email"
              value={values.email}
              onChange={handleChange}
              onBlur={() => {
                setTouched({
                  ...touched,
                  email: true,
                });
              }}
              invalid={touched.email && !!errors.email}
            />
            {touched.email && !!errors.email && (
              <InputDescription invalid description={errors.email} />
            )}
          </Box>
          {showInviteOption && (
            <Box marginBottom={16}>
              <Checkbox
                testId="send-snippet-invite"
                checked={values.invite}
                onChange={checked => setValues({ ...values, invite: checked })}
                label={intl.formatMessage({
                  defaultMessage: 'Invite to join my team',
                  id: 'yTZcpE',
                })}
              />
            </Box>
          )}
          <Box marginBottom={16}>
            <InputLabel htmlFor="message" optional>
              <FormattedMessage defaultMessage="Want to include a message?" id="FF7/3H" />
            </InputLabel>
            <TextArea
              id="message"
              value={values.message}
              onChange={handleChange}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter your message here',
                id: '+3K44W',
              })}
            />
            <InputDescription
              description={intl.formatMessage({
                defaultMessage: "You'll be cc'd on this email.",
                description: "CC'd means we'll send a copy of the email.",
                id: '5XP3Cx',
              })}
            />
          </Box>
          <Flex justifyContent="flex-end">
            <Button type="submit" variant="fill-primary">
              {mode == 'snippet' ? (
                <FormattedMessage defaultMessage="Send your snippet" id="NbMq55" />
              ) : (
                <FormattedMessage defaultMessage="Send" id="9WRlF4" />
              )}
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
